import { useCallback, useEffect } from "react";
import { Box, ScrollArea, Text, Stack, Group, Divider, Button, Checkbox, Switch, NumberInput, Select } from "@mantine/core";
import {
  useViewerStore,
  useEditLinksMode,
  useEditLinksInstanceId,
  useEditLinksModuleId,
  useEditLinksData,
  useEditLinksCanBeChild,
  useEditLinksHasServo,
  useEditLinksEndEffectorMode,
  useEditLinksEndEffectorLink,
  useEditLinksEndEffectorOffsetXyz,
  useEditLinksEndEffectorOffsetRpy,
  useEditLinksCanEditEndEffector,
} from "../../store";
import { useWebSocket } from "../../WebSocket";
import { getJointUnit } from "./jointUnits";

export function EditLinksPanel() {
  const editLinksMode = useEditLinksMode();
  const editLinksInstanceId = useEditLinksInstanceId();
  const editLinksModuleId = useEditLinksModuleId();
  const editLinksData = useEditLinksData();
  const editLinksCanBeChild = useEditLinksCanBeChild();
  const editLinksHasServo = useEditLinksHasServo();
  const editLinksEndEffectorMode = useEditLinksEndEffectorMode();
  const editLinksEndEffectorLink = useEditLinksEndEffectorLink();
  const editLinksEndEffectorOffsetXyz = useEditLinksEndEffectorOffsetXyz();
  const editLinksEndEffectorOffsetRpy = useEditLinksEndEffectorOffsetRpy();
  const editLinksCanEditEndEffector = useEditLinksCanEditEndEffector();
  const toggleLinkConnectable = useViewerStore((state) => state.toggleLinkConnectable);
  const setEditLinksCanBeChild = useViewerStore((state) => state.setEditLinksCanBeChild);
  const setEditLinksHasServo = useViewerStore((state) => state.setEditLinksHasServo);
  const setEditLinksEndEffectorMode = useViewerStore((state) => state.setEditLinksEndEffectorMode);
  const setEditLinksEndEffectorLink = useViewerStore((state) => state.setEditLinksEndEffectorLink);
  const setEditLinksEndEffectorOffsetXyz = useViewerStore((state) => state.setEditLinksEndEffectorOffsetXyz);
  const setEditLinksEndEffectorOffsetRpy = useViewerStore((state) => state.setEditLinksEndEffectorOffsetRpy);
  const exitEditLinksMode = useViewerStore((state) => state.exitEditLinksMode);
  const { sendExitEditLinksMode, sendSaveConnectableLinks, sendShowEndCoordsGizmo } = useWebSocket();
  const rotationUnit = getJointUnit("revolute");
  const isCustomEndEffector = editLinksEndEffectorMode === "custom" && !!editLinksEndEffectorLink;

  const hideEndEffectorGizmo = useCallback(() => {
    sendShowEndCoordsGizmo(false, "", [0, 0, 0], [0, 0, 0]);
  }, [sendShowEndCoordsGizmo]);

  const showEndEffectorGizmo = useCallback(
    (
      linkName: string,
      position: [number, number, number],
      rotation: [number, number, number]
    ) => {
      if (!editLinksInstanceId) {
        return;
      }
      sendShowEndCoordsGizmo(true, `${editLinksInstanceId}:${linkName}`, position, rotation);
    },
    [editLinksInstanceId, sendShowEndCoordsGizmo]
  );

  useEffect(() => {
    if (!editLinksMode || !isCustomEndEffector) {
      hideEndEffectorGizmo();
      return;
    }
    showEndEffectorGizmo(
      editLinksEndEffectorLink as string,
      editLinksEndEffectorOffsetXyz,
      editLinksEndEffectorOffsetRpy
    );
  }, [
    editLinksMode,
    isCustomEndEffector,
    editLinksEndEffectorLink,
    hideEndEffectorGizmo,
    showEndEffectorGizmo,
  ]);

  const handleSave = () => {
    if (editLinksModuleId) {
      const connectableLinks = editLinksData
        .filter((link) => link.is_connectable)
        .map((link) => link.name);
      sendSaveConnectableLinks(
        editLinksModuleId,
        connectableLinks,
        editLinksCanBeChild,
        editLinksHasServo,
        editLinksEndEffectorMode,
        editLinksEndEffectorLink,
        editLinksEndEffectorOffsetXyz,
        editLinksEndEffectorOffsetRpy,
      );
    }
    hideEndEffectorGizmo();
    sendExitEditLinksMode();
    exitEditLinksMode();
  };

  const handleCancel = () => {
    hideEndEffectorGizmo();
    sendExitEditLinksMode();
    exitEditLinksMode();
  };

  if (!editLinksMode) {
    return null;
  }

  const endEffectorOptions = [
    { value: "__auto__", label: "Auto - a gizmo per leaf link" },
    { value: "__none__", label: "None - no gizmos" },
    ...editLinksData.map((link) => ({ value: link.name, label: link.name })),
  ];
  const selectedEndEffectorValue =
    editLinksEndEffectorMode === "custom" && editLinksEndEffectorLink
      ? editLinksEndEffectorLink
      : editLinksEndEffectorMode === "none"
        ? "__none__"
        : "__auto__";

  const handleEndEffectorOffsetXyzChange = (axis: 0 | 1 | 2, value: number | string) => {
    const next: [number, number, number] = [...editLinksEndEffectorOffsetXyz];
    next[axis] = typeof value === "number" ? value : 0;
    setEditLinksEndEffectorOffsetXyz(next);
    if (isCustomEndEffector) {
      showEndEffectorGizmo(editLinksEndEffectorLink as string, next, editLinksEndEffectorOffsetRpy);
    }
  };

  const handleEndEffectorOffsetRpyChange = (axis: 0 | 1 | 2, value: number | string) => {
    const next: [number, number, number] = [...editLinksEndEffectorOffsetRpy];
    const display = typeof value === "number" ? value : 0;
    next[axis] = rotationUnit.toNative(display);
    setEditLinksEndEffectorOffsetRpy(next);
    if (isCustomEndEffector) {
      showEndEffectorGizmo(editLinksEndEffectorLink as string, editLinksEndEffectorOffsetXyz, next);
    }
  };

  return (
    <Box
      style={{
        position: "absolute",
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        backgroundColor: "var(--mantine-color-body)",
        zIndex: 100,
        display: "flex",
        flexDirection: "column",
      }}
    >
      <Box p="md" style={{ borderBottom: "1px solid var(--mantine-color-gray-3)" }}>
        <Text size="lg" fw={600}>
          Edit Connectable Links
        </Text>
        <Text size="xs" c="dimmed">
          {editLinksModuleId}
        </Text>
      </Box>

      <ScrollArea style={{ flex: 1 }} p="sm">
        <Stack gap="md">
          {/* Can Be Child Toggle */}
          <Box>
            <Switch
              label="Can be child"
              description="Allow this module to connect to a parent module. Disable for root-only modules (e.g., base modules)."
              checked={editLinksCanBeChild}
              onChange={(e) => setEditLinksCanBeChild(e.currentTarget.checked)}
              size="sm"
            />
          </Box>

          {/* Has Servo Toggle */}
          <Box>
            <Switch
              label="Has servo"
              description="Module contains servo motor(s). Modules without servos don't contribute to servo mapping."
              checked={editLinksHasServo}
              onChange={(e) => setEditLinksHasServo(e.currentTarget.checked)}
              size="sm"
            />
          </Box>

          {editLinksCanEditEndEffector && (
            <>
              <Divider />

              <Box>
                <Text size="xs" c="dimmed" mb="xs">
                  Module end effector (for IK)
                </Text>
                <Stack gap="xs">
                  <Select
                    label="Link"
                    data={endEffectorOptions}
                    value={selectedEndEffectorValue}
                    onChange={(value) => {
                      if (!value || value === "__auto__") {
                        setEditLinksEndEffectorMode("auto");
                        setEditLinksEndEffectorLink(null);
                        hideEndEffectorGizmo();
                        return;
                      }
                      if (value === "__none__") {
                        setEditLinksEndEffectorMode("none");
                        setEditLinksEndEffectorLink(null);
                        hideEndEffectorGizmo();
                        return;
                      }
                      setEditLinksEndEffectorMode("custom");
                      setEditLinksEndEffectorLink(value);
                      showEndEffectorGizmo(
                        value,
                        editLinksEndEffectorOffsetXyz,
                        editLinksEndEffectorOffsetRpy
                      );
                    }}
                    allowDeselect={false}
                    size="xs"
                  />
                  {isCustomEndEffector && (
                    <>
                      <Text size="xs" c="dimmed">Offset XYZ (m)</Text>
                      <Group grow>
                        <NumberInput
                          label="X"
                          value={editLinksEndEffectorOffsetXyz[0]}
                          onChange={(value) => handleEndEffectorOffsetXyzChange(0, value)}
                          size="xs"
                          step={0.001}
                          decimalScale={4}
                          hideControls
                        />
                        <NumberInput
                          label="Y"
                          value={editLinksEndEffectorOffsetXyz[1]}
                          onChange={(value) => handleEndEffectorOffsetXyzChange(1, value)}
                          size="xs"
                          step={0.001}
                          decimalScale={4}
                          hideControls
                        />
                        <NumberInput
                          label="Z"
                          value={editLinksEndEffectorOffsetXyz[2]}
                          onChange={(value) => handleEndEffectorOffsetXyzChange(2, value)}
                          size="xs"
                          step={0.001}
                          decimalScale={4}
                          hideControls
                        />
                      </Group>
                      <Text size="xs" c="dimmed">Offset RPY (deg)</Text>
                      <Group grow>
                        <NumberInput
                          label="Roll"
                          value={rotationUnit.toDisplay(editLinksEndEffectorOffsetRpy[0])}
                          onChange={(value) => handleEndEffectorOffsetRpyChange(0, value)}
                          size="xs"
                          step={rotationUnit.step}
                          decimalScale={rotationUnit.decimals}
                          hideControls
                        />
                        <NumberInput
                          label="Pitch"
                          value={rotationUnit.toDisplay(editLinksEndEffectorOffsetRpy[1])}
                          onChange={(value) => handleEndEffectorOffsetRpyChange(1, value)}
                          size="xs"
                          step={rotationUnit.step}
                          decimalScale={rotationUnit.decimals}
                          hideControls
                        />
                        <NumberInput
                          label="Yaw"
                          value={rotationUnit.toDisplay(editLinksEndEffectorOffsetRpy[2])}
                          onChange={(value) => handleEndEffectorOffsetRpyChange(2, value)}
                          size="xs"
                          step={rotationUnit.step}
                          decimalScale={rotationUnit.decimals}
                          hideControls
                        />
                      </Group>
                    </>
                  )}
                </Stack>
              </Box>
            </>
          )}

          <Divider />

          {/* Connectable Links */}
          <Box>
            <Text size="xs" c="dimmed" mb="xs">
              Select which links can accept child modules:
            </Text>
            <Stack gap="xs">
              {editLinksData.map((link) => (
                <Checkbox
                  key={link.name}
                  label={link.name}
                  checked={link.is_connectable}
                  onChange={() => toggleLinkConnectable(link.name)}
                  size="sm"
                />
              ))}
            </Stack>
          </Box>
        </Stack>
      </ScrollArea>

      <Box p="md" style={{ borderTop: "1px solid var(--mantine-color-gray-3)" }}>
        <Group justify="flex-end" gap="sm">
          <Button variant="default" size="sm" onClick={handleCancel}>
            Cancel
          </Button>
          <Button color="blue" size="sm" onClick={handleSave}>
            Save
          </Button>
        </Group>
      </Box>
    </Box>
  );
}

